import React from 'react';
import DOMPurify from 'dompurify';

import { makeStyles } from "@material-ui/core/styles";
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import TextField from '@material-ui/core/TextField';

import Button from '@material-ui/core/Button';
import RefreshIcon from '@material-ui/icons/Refresh';

import Badge from '@material-ui/core/Badge';
import { getBadgeContent, isVariant, getImageUrl, hasValidImage } from './utils';
import Pagination from './Pagination';

const useStyles = makeStyles(theme => ({
    webPlant: {
        padding: '10px 10px 5px 5px',
        display: 'inline-flex',
        [theme.breakpoints.down('xs')]: {
            padding: '7px 8px 0px 8px'
        }
    },
    separator: {
        paddingTop: '10px'
    },
    comicsContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'flex-start',
        [theme.breakpoints.down('xs')]: {
            justifyContent: 'center'
        }
    },
    comicCard: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100px',
        textAlign: 'center'
    },
    comicImage: {
        width: '100px',
        height: '150px',
        objectFit: 'cover',
        display: 'block'
    },
    comicPlaceholder: {
        width: '100px',
        height: '150px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f0f0f0',
        border: '1px solid #ccc',
        textAlign: 'center',
        padding: '8px',
        boxSizing: 'border-box',
        fontSize: '0.7em',
        wordWrap: 'break-word',
        overflow: 'hidden'
    },
    comicTitle: {
        width: '100px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontSize: '0.75em',
        marginTop: '4px'
    },
    refreshButton: {
        marginBottom: '15px'
    },
    dialogTitle: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    },
    descriptionSection: {
        marginBottom: '20px',
        fontSize: '0.95em',
        lineHeight: '1.5',
        '& p': {
            margin: '0 0 10px 0'
        },
        '& a': {
            color: theme.palette.primary.main,
            textDecoration: 'none',
            '&:hover': {
                textDecoration: 'underline'
            }
        }
    },
    comicDetailsSection: {
        marginBottom: '16px',
        padding: '12px',
        backgroundColor: theme.palette.grey[100],
        borderRadius: '8px'
    },
    detailRow: {
        display: 'flex',
        marginBottom: '8px',
        '&:last-child': {
            marginBottom: 0
        }
    },
    detailLabel: {
        fontWeight: 600,
        minWidth: '100px',
        color: theme.palette.text.secondary
    },
    detailValue: {
        color: theme.palette.text.primary
    },
    coversGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
        gap: '15px',
        marginTop: '20px',
        [theme.breakpoints.down('sm')]: {
            gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
            gap: '10px'
        }
    },
    coverItem: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
    },
    coverImageWrapper: {
        position: 'relative',
        cursor: 'pointer'
    },
    coverImage: {
        width: '100%',
        height: 'auto',
        borderRadius: '4px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
    },
    coverImageOwned: {
        width: '100%',
        height: 'auto',
        borderRadius: '4px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        filter: 'saturate(1.5)'
    },
    coverImageNotOwned: {
        width: '100%',
        height: 'auto',
        borderRadius: '4px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        filter: 'saturate(0.2)'
    },
    coverLabel: {
        marginTop: '5px',
        fontSize: '0.75em',
        textAlign: 'center',
        color: '#666'
    },
    customImageSection: {
        marginTop: '20px',
        padding: '16px',
        backgroundColor: '#f5f5f5',
        borderRadius: '8px'
    },
    customImageField: {
        width: '100%',
        marginBottom: '8px'
    },
    customImageButton: {
        marginTop: '8px'
    }
}));


const Comics = (props) => {
    const classes = useStyles();
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [selectedComic, setSelectedComic] = React.useState(null);
    
    // Pagination state
    const [currentPage, setCurrentPage] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(50);

    const handleComicClick = (comic) => {
        setSelectedComic(comic);
        setDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setDialogOpen(false);
        setSelectedComic(null);
    };

    const handleReload = () => {
        if (props.currentSeries && props.addAllComics) {
            props.addAllComics(props.currentSeries);
        }
    };
    
    // Reset pagination when comics list changes
    React.useEffect(() => {
        setCurrentPage(1);
    }, [props.comics.length]);

    // Filter to show only main issues (not variants)
    const mainComics = props.comics.filter(comic => !isVariant(comic));
    
    // Sort comics by issue number
    const sortedComics = React.useMemo(() => {
        return [...mainComics].sort((a, b) => {
            if (b.issueNumber > a.issueNumber) { return -1; }
            if (b.issueNumber < a.issueNumber) { return 1; }
            return 0;
        });
    }, [mainComics]);
    
    // Apply pagination
    const paginatedComics = React.useMemo(() => {
        const startIndex = (currentPage - 1) * pageSize;
        return sortedComics.slice(startIndex, startIndex + pageSize);
    }, [sortedComics, currentPage, pageSize]);

    // Check if an issue is owned (main comic or any variant is owned)
    const isIssueOwned = (issueNumber) => {
        return props.comics.some(comic => 
            comic.issueNumber === issueNumber && comic.gotIt
        );
    };
    
    // Shared pagination props to avoid duplication
    const paginationProps = {
        currentPage,
        totalItems: mainComics.length,
        pageSize,
        onPageChange: setCurrentPage,
        onPageSizeChange: setPageSize
    };

    return (
        <div>
            {props.currentSeries && props.addAllComics && (
                <Button 
                    variant="contained" 
                    color="primary" 
                    startIcon={<RefreshIcon />}
                    onClick={handleReload}
                    className={classes.refreshButton}
                >
                    Reload Series
                </Button>
            )}
            
            {mainComics.length > 0 && (
                <Pagination {...paginationProps} />
            )}
            
            <div className={classes.comicsContainer}>
                {paginatedComics.map(card => {
                    const owned = isIssueOwned(card.issueNumber);
                    return <Comic 
                        key={card.id} 
                        {...card} 
                        gotIt={owned}
                        setCurrentComic={handleComicClick} 
                    />;
                })}
            </div>
            
            {mainComics.length > pageSize && (
                <Pagination {...paginationProps} />
            )}
            
            {selectedComic && (
                <ComicDialog 
                    open={dialogOpen}
                    onClose={handleCloseDialog}
                    comic={selectedComic}
                    allComics={props.comics}
                    toggleOwnership={props.toggleOwnership}
                    updateSeriesImage={props.updateSeriesImage}
                />
            )}
        </div>
    );
};

const Comic = (props) => {
    const classes = useStyles();
    const hasImage = hasValidImage(props);
    
    return (
        <div className={classes.webPlant}>
            <div className={props.gotIt ? "cardwrapper_highlight" : "cardwrapper_nohighlight"}>
                <div className={classes.comicCard} onClick={() => props.setCurrentComic(props)}>
                    <Badge badgeContent={getBadgeContent(props.issueNumber, props.variantDescription)} color="primary" max={9999}>
                        {hasImage ? (
                            <img 
                                className={classes.comicImage}
                                src={getImageUrl(props, 'portrait_medium')} 
                                alt={props.title} 
                                title={props.title} 
                            />
                        ) : (
                            <div className={classes.comicPlaceholder}>
                                {props.title || (props.issueNumber != null ? `Issue #${props.issueNumber}` : 'Comic')}
                            </div>
                        )}
                    </Badge>
                    <div className={classes.comicTitle}>{props.title}</div>
                </div>
            </div>
        </div>
    );
};

const ComicDialog = (props) => {
    const classes = useStyles();
    const { comic, allComics, open, onClose, toggleOwnership, updateSeriesImage } = props;
    
    // Local state to track ownership status for immediate UI updates
    const [localOwnership, setLocalOwnership] = React.useState({});
    // State for custom image URL
    const [customImageUrl, setCustomImageUrl] = React.useState('');
    
    // Get all comics for this issue (main + variants) using useMemo to avoid recreating on every render
    const allIssueComics = React.useMemo(() => {
        const variants = allComics.filter(c => 
            c.issueNumber === comic.issueNumber && isVariant(c)
        );
        return [comic, ...variants];
    }, [comic, allComics]);
    
    // Initialize local ownership state when dialog opens or comics change
    React.useEffect(() => {
        const variants = allComics.filter(c => 
            c.issueNumber === comic.issueNumber && isVariant(c)
        );
        const issueComics = [comic, ...variants];
        
        const ownership = {};
        issueComics.forEach(c => {
            ownership[c.id] = c.gotIt || false;
        });
        setLocalOwnership(ownership);
    }, [comic, allComics]);
    
    const handleToggleOwnership = (comicToToggle) => {
        // Update local state immediately for instant UI feedback
        setLocalOwnership(prev => ({
            ...prev,
            [comicToToggle.id]: !prev[comicToToggle.id]
        }));
        
        // Call the parent's toggle function to update the backend
        if (toggleOwnership) {
            toggleOwnership(comicToToggle);
        }
    };

    const handleSetCustomImage = () => {
        if (customImageUrl.trim() && updateSeriesImage) {
            updateSeriesImage(customImageUrl.trim());
            setCustomImageUrl('');
        }
    };

    return (
        <Dialog 
            open={open} 
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                style: {
                    maxWidth: '100%',
                    width: 'auto',
                    margin: '16px'
                }
            }}
        >
            <DialogTitle className={classes.dialogTitle}>
                <span>Issue #{comic.issueNumber}</span>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                {/* Comic Details Section */}
                <div className={classes.comicDetailsSection}>
                    {comic.title && (
                        <div className={classes.detailRow}>
                            <span className={classes.detailLabel}>Title:</span>
                            <span className={classes.detailValue}>{comic.title}</span>
                        </div>
                    )}
                    {comic.series?.name && (
                        <div className={classes.detailRow}>
                            <span className={classes.detailLabel}>Series:</span>
                            <span className={classes.detailValue}>{comic.series.name}</span>
                        </div>
                    )}
                    {comic.coverDate && (
                        <div className={classes.detailRow}>
                            <span className={classes.detailLabel}>Cover Date:</span>
                            <span className={classes.detailValue}>{comic.coverDate}</span>
                        </div>
                    )}
                </div>

                {/* Description - only from main version, rendered as sanitized HTML */}
                {comic.description && (
                    <div 
                        className={classes.descriptionSection}
                        dangerouslySetInnerHTML={{ 
                            __html: DOMPurify.sanitize(comic.description, { 
                                ALLOWED_TAGS: ['p', 'strong', 'em', 'b', 'i', 'a', 'br', 'ul', 'ol', 'li'],
                                ALLOWED_ATTR: ['href']
                            }) 
                        }}
                    />
                )}

                {/* Covers Grid */}
                <div className={classes.coversGrid}>
                    {allIssueComics.map((issueComic) => (
                        <div key={issueComic.id} className={classes.coverItem}>
                            <div 
                                className={classes.coverImageWrapper}
                                onClick={() => handleToggleOwnership(issueComic)}
                            >
                                <img 
                                    className={localOwnership[issueComic.id] ? classes.coverImageOwned : classes.coverImageNotOwned}
                                    src={getImageUrl(issueComic, 'portrait_xlarge')} 
                                    alt={issueComic.title} 
                                    title={issueComic.title} 
                                />
                            </div>
                            {issueComic.variantDescription && (
                                <div className={classes.coverLabel}>
                                    {issueComic.variantDescription}
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* Custom Image URL Section */}
                {updateSeriesImage && (
                    <div className={classes.customImageSection}>
                        <TextField
                            className={classes.customImageField}
                            variant="outlined"
                            size="small"
                            label="Custom Series Image URL"
                            placeholder="Enter image URL..."
                            value={customImageUrl}
                            onChange={(e) => setCustomImageUrl(e.target.value)}
                            onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                    handleSetCustomImage();
                                }
                            }}
                        />
                        <Button
                            className={classes.customImageButton}
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={handleSetCustomImage}
                            disabled={!customImageUrl.trim()}
                        >
                            Set Image
                        </Button>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default Comics;